import type { TAssetCachePlaybackPurgeReason } from '@lucimark/shared/helpers/assetCachePlaybackPurges'
/**
 * Client-side health checks for campaign binaries stored in IndexedDB before playback.
 *
 * **Extend here when adding formats or stricter validation:**
 * - Magic-byte sniffing: `sniffPlaybackKind` (add a branch before `unknown`).
 * - MIME repair: `mimeForSniff` + `needsImageMimeRepair` / `needsVideoMimeRepair` / `claimsOctetOrEmpty` block.
 * - Purge rules: use `ASSET_CACHE_PLAYBACK_PURGE_REASON` so logs and Sentry stay consistent.
 */
import type { ICachedAsset } from '@lucimark/shared/models/asset'
import {
  ASSET_CACHE_PLAYBACK_PURGE_REASON

} from '@lucimark/shared/helpers/assetCachePlaybackPurges'

const GZIP_MAGIC: readonly [number, number] = [0x1F, 0x8B]

export type TBinaryCachePlaybackAssessment
  = | { action: 'keep' }
    | { action: 'repair', asset: ICachedAsset }
    | { action: 'purge', reason: TAssetCachePlaybackPurgeReason }

const PNG_SIG = Uint8Array.of(0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A)

function bytesMatchPrefix(data: Uint8Array, prefix: Uint8Array): boolean {
  if (data.length < prefix.length) {
    return false
  }
  for (let i = 0; i < prefix.length; i++) {
    if (data[i] !== prefix[i]) {
      return false
    }
  }
  return true
}

function looksLikeJpeg(data: Uint8Array): boolean {
  return data.length >= 3 && data[0] === 0xFF && data[1] === 0xD8 && data[2] === 0xFF
}

function looksLikeGif(data: Uint8Array): boolean {
  if (data.length < 6) {
    return false
  }
  const g = data[0] === 0x47 && data[1] === 0x49 && data[2] === 0x46
  const a = data[3] === 0x38 && (data[4] === 0x37 || data[4] === 0x39) && data[5] === 0x61
  return g && a
}

function looksLikeWebp(data: Uint8Array): boolean {
  if (data.length < 12) {
    return false
  }
  const riff = data[0] === 0x52 && data[1] === 0x49 && data[2] === 0x46 && data[3] === 0x46
  const webp = data[8] === 0x57 && data[9] === 0x45 && data[10] === 0x42 && data[11] === 0x50
  return riff && webp
}

function looksLikeWebm(data: Uint8Array): boolean {
  return data.length >= 4 && data[0] === 0x1A && data[1] === 0x45 && data[2] === 0xDF && data[3] === 0xA3
}

function looksLikeMp4(data: Uint8Array): boolean {
  return data.length >= 8 && data[4] === 0x66 && data[5] === 0x74 && data[6] === 0x79 && data[7] === 0x70
}

function isGzipMagic(data: Uint8Array): boolean {
  return data.length >= 2 && data[0] === GZIP_MAGIC[0] && data[1] === GZIP_MAGIC[1]
}

/** True if bytes look like an HTML or XML document (common API error body). */
function looksLikeMarkupOrJsonError(data: Uint8Array, textSampleMax = 512): boolean {
  const n = Math.min(data.length, textSampleMax)
  if (n === 0) {
    return false
  }
  let start = 0
  while (start < n && (data[start] === 0x20 || data[start] === 0x09 || data[start] === 0x0D || data[start] === 0x0A)) {
    start++
  }
  if (start >= n) {
    return false
  }
  if (data[start] === 0x3C) {
    try {
      const decoder = new TextDecoder('utf-8', { fatal: false })
      const text = decoder.decode(data.slice(start, n)).trimStart().toLowerCase()
      return text.startsWith('<!doctype') || text.startsWith('<html') || text.startsWith('<?xml')
    }
    catch {
      return true
    }
  }
  if (data[start] === 0x7B) {
    try {
      const decoder = new TextDecoder('utf-8', { fatal: false })
      const text = decoder.decode(data.slice(start, Math.min(n, start + 400)))
      return /"error"\s*:/i.test(text)
        || (/"message"\s*:/i.test(text) && /"status"\s*:/i.test(text))
    }
    catch {
      return false
    }
  }
  return false
}

function sniffPlaybackKind(header: Uint8Array): 'gzip' | 'png' | 'jpeg' | 'gif' | 'webp' | 'webm' | 'mp4' | 'unknown' {
  if (isGzipMagic(header)) {
    return 'gzip'
  }
  if (bytesMatchPrefix(header, PNG_SIG)) {
    return 'png'
  }
  if (looksLikeJpeg(header)) {
    return 'jpeg'
  }
  if (looksLikeGif(header)) {
    return 'gif'
  }
  if (looksLikeWebp(header)) {
    return 'webp'
  }
  if (looksLikeWebm(header)) {
    return 'webm'
  }
  if (looksLikeMp4(header)) {
    return 'mp4'
  }
  return 'unknown'
}

function mimeForSniff(kind: Exclude<ReturnType<typeof sniffPlaybackKind>, 'gzip' | 'unknown'>): string {
  switch (kind) {
    case 'png':
      return 'image/png'
    case 'jpeg':
      return 'image/jpeg'
    case 'gif':
      return 'image/gif'
    case 'webp':
      return 'image/webp'
    case 'webm':
      return 'video/webm'
    case 'mp4':
      return 'video/mp4'
  }
}

function needsImageMimeRepair(blobType: string, kind: ReturnType<typeof sniffPlaybackKind>): boolean {
  if (kind === 'png' || kind === 'jpeg' || kind === 'gif' || kind === 'webp') {
    return !blobType.includes('image')
  }
  return false
}

function needsVideoMimeRepair(blobType: string, kind: ReturnType<typeof sniffPlaybackKind>): boolean {
  if (kind === 'webm' || kind === 'mp4') {
    return !blobType.includes('video')
  }
  return false
}

function repairedAsset(base: ICachedAsset, blob: Blob): ICachedAsset {
  return {
    ...base,
    blob,
    type: blob.type,
    size: blob.size
  }
}

const HEADER_READ = 32
/**
 * How far into a JPEG to scan for the SOF marker when checking for CMYK. Large EXIF or ICC
 *  APP segments can push the SOF past the first few KB, so we use a generous window.
 */
const JPEG_CMYK_SCAN_BYTES = 64 * 1024

/**
 * Scan a JPEG blob for a 4-component SOF (Start of Frame) segment.
 * A component count of 4 indicates CMYK or YCCK color space, which Chromium-based browsers
 * (including Tauri's WebView2 on Windows) render as a solid black image.
 * Returns false — the safe default — if no SOF is found within the scan window.
 */
async function scanJpegForCmyk(blob: Blob): Promise<boolean> {
  const size = Math.min(blob.size, JPEG_CMYK_SCAN_BYTES)
  const buf = await blob.slice(0, size).arrayBuffer()
  const data = new Uint8Array(buf)
  let i = 2 // skip SOI (0xFF 0xD8)
  while (i + 3 < data.length) {
    if (data[i] !== 0xFF) { break }
    const marker = data[i + 1]
    if (marker === 0xD9 || marker === 0xDA) { break } // EOI / SOS — no more APP/SOF segments
    const segLen = (data[i + 2] << 8) | data[i + 3]
    if (segLen < 2) { break } // malformed length
    // SOF markers: 0xC0–0xCF excluding DHT(0xC4), JPG(0xC8), DAC(0xCC)
    const isSOF = marker >= 0xC0 && marker <= 0xCF
      && marker !== 0xC4 && marker !== 0xC8 && marker !== 0xCC
    if (isSOF) {
      // SOF layout: [0xFF][SOF][len_hi][len_lo][precision][height_hi][height_lo][width_hi][width_lo][n_components]
      // n_components is at i+9
      return i + 9 < data.length && data[i + 9] === 4
    }
    i += 2 + segLen
  }
  return false
}

/**
 * Decide whether a cached binary blob is usable for player `VBuffer` / `buildAssetIndex` playback.
 * - Purges empty bodies and obvious HTML/JSON error pages when MIME claims raster/video.
 * - Repairs image/video MIME when magic bytes match but `Content-Type` was wrong (e.g. octet-stream).
 * - Leaves ambiguous blobs unchanged (avoids false positives).
 * - Keeps Android file-backed MP4 rows (`nativeFilePath`, no IDB blob) — disk presence is checked in AssetsService.
 */
export async function assessCachedBinaryForPlayback(asset: ICachedAsset): Promise<TBinaryCachePlaybackAssessment> {
  if (typeof asset.nativeFilePath === 'string' && asset.nativeFilePath.trim() !== '') {
    if (asset.size <= 0) {
      return { action: 'purge', reason: ASSET_CACHE_PLAYBACK_PURGE_REASON.EMPTY_BODY }
    }
    return { action: 'keep' }
  }

  const blob = asset.blob
  if (blob == null) {
    return { action: 'purge', reason: ASSET_CACHE_PLAYBACK_PURGE_REASON.EMPTY_BODY }
  }
  const declaredType = asset.type
  if (blob.size === 0) {
    return { action: 'purge', reason: ASSET_CACHE_PLAYBACK_PURGE_REASON.EMPTY_BODY }
  }

  const headerBuf = await blob.slice(0, Math.min(HEADER_READ, blob.size)).arrayBuffer()
  const header = new Uint8Array(headerBuf)
  const kind = sniffPlaybackKind(header)

  if (kind === 'gzip') {
    return { action: 'keep' }
  }

  if (kind === 'jpeg' && await scanJpegForCmyk(blob)) {
    return { action: 'purge', reason: ASSET_CACHE_PLAYBACK_PURGE_REASON.JPEG_CMYK_COLOR_SPACE }
  }

  const t = declaredType.toLowerCase()
  const claimsRasterOrVideo = t.includes('image') || t.includes('video')
  const claimsOctetOrEmpty = t === '' || t === 'application/octet-stream' || t === 'binary/octet-stream'

  if (claimsRasterOrVideo && looksLikeMarkupOrJsonError(header)) {
    return { action: 'purge', reason: ASSET_CACHE_PLAYBACK_PURGE_REASON.HTML_OR_JSON_ERROR_UNDER_MEDIA_MIME }
  }

  if (t.includes('text/html')) {
    if (looksLikeMarkupOrJsonError(header) || header.length > 0 && header[0] === 0x3C) {
      return { action: 'keep' }
    }
    return { action: 'purge', reason: ASSET_CACHE_PLAYBACK_PURGE_REASON.DECLARED_HTML_BUT_NOT_HTML }
  }

  if (needsImageMimeRepair(declaredType, kind)) {
    if (kind === 'png' || kind === 'jpeg' || kind === 'gif' || kind === 'webp') {
      const mime = mimeForSniff(kind)
      const next = new Blob([await blob.arrayBuffer()], { type: mime })
      return { action: 'repair', asset: repairedAsset(asset, next) }
    }
  }

  if (needsVideoMimeRepair(declaredType, kind)) {
    if (kind === 'webm' || kind === 'mp4') {
      const mime = mimeForSniff(kind)
      const next = new Blob([await blob.arrayBuffer()], { type: mime })
      return { action: 'repair', asset: repairedAsset(asset, next) }
    }
  }

  if (claimsOctetOrEmpty) {
    if (kind === 'png' || kind === 'jpeg' || kind === 'gif' || kind === 'webp') {
      const mime = mimeForSniff(kind)
      const next = new Blob([await blob.arrayBuffer()], { type: mime })
      return { action: 'repair', asset: repairedAsset(asset, next) }
    }
    if (kind === 'webm' || kind === 'mp4') {
      const mime = mimeForSniff(kind)
      const next = new Blob([await blob.arrayBuffer()], { type: mime })
      return { action: 'repair', asset: repairedAsset(asset, next) }
    }
  }

  return { action: 'keep' }
}
